// Send history rows (F layout) from events/<E>/wa_runs, and "who is next" for the Send page.
import type { LedgerRow, Run } from "@/data/types";
import { type CapUsage, isOverLimit, isReleased } from "./cap";
import type { Person, Reg } from "./people";

/** A run with no finished_at older than this is shown as "did not finish", not "sending". */
export const RUN_STALE_MS = 3 * 3600e3;

export type RunState = "running" | "done" | "stopped" | "unfinished";

export interface RunRow {
  run: Run;
  /** chronological: Batch 1 is the oldest */
  label: string;
  state: RunState;
  stateText: string;
  people: number;
  messages: number;
  /** ledger rows sent during the run that are now delivered or read */
  delivered: number;
  failed: number;
}

export function stopReasonText(reason: string | null): string {
  if (reason == null) return "Finished";
  if (reason === "limit") return "Stopped · reached --limit";
  if (reason === "consecutive_failures") return "Stopped · 3 failures in a row";
  if (reason === "send_blocked") return "Stopped · send blocked";
  if (reason === "transport_error") return "Stopped · network error";
  if (reason === "interrupted") return "Stopped by hand (Ctrl-C)";
  if (reason.startsWith("meta_error_")) return `Stopped · WhatsApp error ${reason.slice("meta_error_".length)}`;
  if (reason.startsWith("error_")) return `Crashed · ${reason.slice("error_".length)}`;
  return `Stopped · ${reason}`;
}

export function runRows(runs: Run[], ledger: LedgerRow[], now: number): RunRow[] {
  const chrono = [...runs].sort((a, b) => (a.startedAt ?? 0) - (b.startedAt ?? 0) || a.id.localeCompare(b.id));
  const rows = chrono.map((run, i): RunRow => {
    const start = run.startedAt ?? 0;
    const end = run.finishedAt ?? now;
    const delivered = ledger.filter(
      (r) =>
        r.sentAt != null && r.sentAt >= start && r.sentAt <= end && (r.status === "delivered" || r.status === "read"),
    ).length;
    let state: RunState;
    let stateText: string;
    if (run.finishedAt == null) {
      const fresh = now - start < RUN_STALE_MS;
      state = fresh ? "running" : "unfinished";
      stateText = fresh ? "Sending" : "Did not finish";
    } else {
      state = run.stopReason == null || run.stopReason === "limit" ? "done" : "stopped";
      stateText = stopReasonText(run.stopReason);
    }
    return {
      run,
      label: `Batch ${i + 1}`,
      state,
      stateText,
      people: run.attemptedPhones,
      messages: run.sent,
      delivered,
      failed: run.failed,
    };
  });
  return rows.reverse();
}

export interface NextBatch {
  /** people (phones) in the next run, in send order */
  list: Person[];
  phones: number;
  /** messages = registrations still to send on those phones; this is `--limit` */
  messages: number;
  families: number;
  /** all phones still waiting */
  waiting: number;
  /** waiting regs per person id, for planned-batch labels */
  order: Map<string, number>;
  /** true when the cap is full and the batch waits for a slot */
  waitsForCap: boolean;
}

/** A registration the next `wa send` would pick up: valid phone, in the sheet, not yet claimed (or re-claimable,
 *  ledger.is_reclaimable: an over-limit row waits for "Retry all"). */
export function isSendable(r: Reg): boolean {
  if (!r.inSheet || !r.phone) return false;
  if (!r.ledger) return true;
  if (r.ledger.status === "invalid") return true;
  return r.ledger.status === "failed" && !r.ledger.wamid && !isOverLimit(r.ledger);
}

const hasReleased = (p: Person) => p.regs.some((r) => isSendable(r) && !!r.ledger && isReleased(r.ledger));

function cmpTs(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a < b ? -1 : 1;
}

/** Same order as the sender: released retries first, then earliest sheet_ts per phone. Phones already in the
 *  cap window are free. */
export function nextBatch(people: Person[], cap: CapUsage): NextBatch {
  const waiting = people
    .filter((p) => !p.optedOut && p.regs.some(isSendable))
    .map((p) => ({
      p,
      ts: p.regs.filter(isSendable).reduce<string | null>((m, r) => (cmpTs(r.sheetTs, m) < 0 ? r.sheetTs : m), null),
    }))
    .sort(
      (a, b) =>
        Number(hasReleased(b.p)) - Number(hasReleased(a.p)) || cmpTs(a.ts, b.ts) || a.p.id.localeCompare(b.p.id),
    )
    .map((x) => x.p);
  const waitsForCap = cap.left === 0;
  const room = waitsForCap ? cap.cap : cap.left;
  const list: Person[] = [];
  let newPhones = 0;
  for (const p of waiting) {
    const free = !!p.phone && cap.phones.has(p.phone);
    if (!free && newPhones >= room) continue;
    if (!free) newPhones++;
    list.push(p);
  }
  return {
    list,
    phones: list.length,
    messages: list.reduce((n, p) => n + p.regs.filter(isSendable).length, 0),
    families: list.filter((p) => p.regs.filter(isSendable).length > 1).length,
    waiting: waiting.length,
    order: new Map(waiting.map((p, i) => [p.id, i])),
    waitsForCap,
  };
}

/** "Batch N · planned" for a waiting person: N counts from the runs so far. */
export function plannedBatch(personId: string, next: NextBatch, runsSoFar: number, cap: CapUsage): number | null {
  const idx = next.order.get(personId);
  if (idx == null) return null;
  const first = next.waitsForCap ? cap.cap : Math.max(cap.left, 1);
  if (idx < first) return runsSoFar + 1;
  return runsSoFar + 2 + Math.floor((idx - first) / Math.max(cap.cap, 1));
}

/** The exact CLI command for the next run (Send is read-only in v1). */
export function sendCommand(eventSlug: string, limit: number): string {
  return `cd services/whatsapp && uv run wa send --event ${eventSlug} --source sheet --production --live --limit ${limit}`;
}

/** The run a send time belongs to (started ≤ t ≤ finished; an open run counts for RUN_STALE_MS). */
export function batchOf(t: number | null | undefined, rows: RunRow[]): RunRow | undefined {
  if (t == null) return undefined;
  return rows.find((r) => {
    const start = r.run.startedAt ?? 0;
    return start <= t && t <= (r.run.finishedAt ?? start + RUN_STALE_MS);
  });
}

// ---------------------------------------------------------------- over-limit retry queue

/** "Retry all" needs a live sender: it acts in the sender's next cycle (every few minutes). */
export const SENDER_FRESH_MS = 10 * 60e3;

/** When the sender last ran a live cycle: its heartbeat (wa_meta/sender), else the newest wa_runs start. */
export function senderLastRan(runs: Run[], heartbeat: number | null): number | null {
  const times = runs.map((r) => r.startedAt).filter((t): t is number => t != null);
  if (heartbeat != null) times.push(heartbeat);
  return times.length ? Math.max(...times) : null;
}

export interface RetryQueue {
  /** ledger keys in the over-limit queue (what "Retry all" sends to wa_retry) */
  keys: string[];
  /** released rows the sender has not picked up yet */
  released: number;
  lastRan: number | null;
  /** the button works: something to release and a sender that ran in the last SENDER_FRESH_MS */
  canRetry: boolean;
  /** why the button is off, for its tooltip; null when it works */
  why: string | null;
}

export function retryQueue(ledger: LedgerRow[], runs: Run[], heartbeat: number | null, now: number): RetryQueue {
  const keys = ledger.filter(isOverLimit).map((r) => r.key);
  const lastRan = senderLastRan(runs, heartbeat);
  const fresh = lastRan != null && now - lastRan <= SENDER_FRESH_MS;
  const why = !keys.length
    ? "Nothing over the limit"
    : !fresh
      ? "The sender has not run in the last 10 minutes. Start `wa send --loop` first."
      : null;
  return { keys, released: ledger.filter(isReleased).length, lastRan, canRetry: why == null, why };
}

/** Copy for the Overview row and its confirm dialog. */
export function retryCopy(q: RetryQueue, now: number) {
  const n = q.keys.length;
  const ran = q.lastRan == null ? "Sender has not run yet" : `Sender last ran ${agoText(now - q.lastRan)}`;
  const msgs = `${n} ${n === 1 ? "message" : "messages"}`;
  return {
    title: `${n} over the limit · waiting for retry`,
    sub: n
      ? `Meta refused these because the 24h limit was full. ${ran}`
      : q.released
        ? `${q.released} queued for resend in the next run. ${ran}`
        : `Nothing refused for the 24h limit. ${ran}`,
    confirmTitle: `Resend ${msgs}?`,
    confirmBody: "The sender sends them first in its next run (within 5 min), if the limit has room.",
  };
}

function agoText(ms: number): string {
  const m = Math.max(Math.round(ms / 60e3), 0);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}
